import React, { useState, useRef, useCallback, useEffect } from "react";
import { Modal, Upload, message, Button } from "antd";
import Cropper from "react-easy-crop";
import { useMutation } from "@apollo/client";
import UserProfileUserIcon from "../Images/userProfileUserIcon.png";
import Warning from "../Images/warningCrop.png";
import Hint from "../Images/cropHint.png";
import "./userProfilePage.css";
import { UPDATE_PROFILE_PIC, REMOVE_PROFILE_PIC } from "../services/userAPI";

const ProfileImageUploader = ({ onImageUpdate, initialImage, resetTrigger, userId }) => {
    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [imageSrc, setImageSrc] = useState(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [uploadedImage, setUploadedImage] = useState(initialImage);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isEditingExistingPhoto, setIsEditingExistingPhoto] = useState(false);
    const [originalFile, setOriginalFile] = useState(null);
    const fileInputRef = useRef(null);

    const [updateProfilePic] = useMutation(UPDATE_PROFILE_PIC, {
        onCompleted: (data) => {
            if (data.updateProfilePic.success) {
                const imageUrl = data.updateProfilePic?.data?.profilePicture;
                // Only update with the server response URL, not the local data URL
                onImageUpdate(imageUrl);
                setUploadedImage(imageUrl);
                message.success("Profile picture updated successfully!");
            } else {
                message.error("Failed to update profile picture: " + data.updateProfilePic.message);
            }
        },
        onError: (error) => {
            console.error("❌ Update GraphQL Error:", error);
            message.error("Update failed: " + error.message);
        },
    });

    const [removeProfilePic, { loading: removeProfilePicLoading }] = useMutation(REMOVE_PROFILE_PIC, {
        onCompleted: (data) => {
            if (data.removeProfilePic.success) {
                onImageUpdate(null);
                setUploadedImage(null);
                message.success("Profile picture removed successfully!");
            } else {
                message.error("Failed to remove profile picture: " + data.removeProfilePic.message);
            }
        },
        onError: (error) => {
            console.error("❌ Remove GraphQL Error:", error);
            message.error("Remove failed: " + error.message);
        },
    });

    const confirmRemovePhoto = async () => {
        if (!userId) {
            message.error("User ID is required.");
            console.error("❌ userId is missing:", userId);
            return;
        }

        try {
            await removeProfilePic({ variables: { userId: parseInt(userId) } });
            resetImageUpload();
            setCropModalVisible(false);
            setConfirmModalVisible(false);
        } catch (error) {
            console.error("❌ Error removing photo:", error);
            message.error("Failed to remove profile picture: " + error.message);
        }
    };

    const handleImageChange = ({ file }) => {

        if (!file) {
            message.error("Invalid file object.");
            return;
        }

        const actualFile = file.originFileObj || file;

        const validFormats = ["image/jpeg", "image/png", "image/jpg",];
        const maxSize = 5 * 1024 * 1024;

        if (!validFormats.includes(actualFile.type)) {
            message.error("Only .jpeg and .png formats are allowed.");
            return;
        }

        if (actualFile.size > maxSize) {
            setFileSizeError(true);
            console.warn("⚠️ File size exceeds limit:", actualFile.size);
        } else {
            setFileSizeError(false);
        }

        setOriginalFile(actualFile);

        const reader = new FileReader();
        reader.onload = (e) => {
            setImageSrc(e.target.result);
            setCropModalVisible(true);
        };
        reader.readAsDataURL(actualFile);
    };

    const onCropComplete = useCallback((_, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const createCanvas = (imageSrc, croppedAreaPixels) => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement("canvas");
            const image = new Image();
            image.crossOrigin = "anonymous";

            image.onload = () => {
                canvas.width = croppedAreaPixels.width;
                canvas.height = croppedAreaPixels.height;
                const ctx = canvas.getContext("2d");

                ctx.drawImage(
                    image,
                    croppedAreaPixels.x,
                    croppedAreaPixels.y,
                    croppedAreaPixels.width,
                    croppedAreaPixels.height,
                    0,
                    0,
                    croppedAreaPixels.width,
                    croppedAreaPixels.height
                );
                resolve(canvas);
            };

            image.onerror = (e) => {
                console.error("❌ Image load error:", e);
                reject(new Error("Failed to load image for cropping"));
            };

            image.src = imageSrc;
        });
    };

    const getCroppedImage = async () => {
        if (fileSizeError) {
            message.error("Cannot save: File size exceeds 5MB limit.");
            return;
        }

        if (!croppedAreaPixels) {
            message.error("Please crop the image first.");
            return;
        }

        if (!userId) {
            message.error("User ID is required.");
            console.error("❌ userId is missing:", userId);
            return;
        }

        try {

            if (!imageSrc) {
                throw new Error("No image source available for cropping.");
            }

            const canvas = await createCanvas(imageSrc, croppedAreaPixels);
            const uniqueFilename = `profile-image-${Date.now()}.jpg`;
            const croppedBlob = await new Promise((resolve, reject) => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            console.error("❌ Failed to create blob from canvas");
                            reject(new Error("Failed to create blob from canvas"));
                            return;
                        }

                        blob.name = uniqueFilename;

                        if (blob.size === 0) {
                            console.error("❌ Created blob has zero size");
                            reject(new Error("Created blob has zero size"));
                            return;
                        }

                        resolve(blob);
                    },
                    "image/jpeg",
                    0.95
                );
            });

            if (!croppedBlob) {
                throw new Error("Failed to create cropped blob");
            }

            const result = await updateProfilePic({
                variables: {
                    document: croppedBlob,
                    userId: parseInt(userId),
                    reasonChanged: "Profile picture updated",
                },
                context: {
                    useMultipart: true,
                },
            });

            setCropModalVisible(false);
            setIsEditingExistingPhoto(false);
            setOriginalFile(null);
            setImageSrc(null); // Clear immediately - don't store data URL

            // Clean up canvas to prevent memory leaks
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                }
            }

        } catch (error) {
            console.error("❌ Error in getCroppedImage:", error);
            console.error("Error stack:", error.stack);
            message.error("Failed to process or upload the cropped image: " + error.message);
        }
    };

    const resetImageUpload = () => {
        // Clean up any existing image sources
        if (imageSrc && (imageSrc.startsWith('data:') || imageSrc.startsWith('blob:'))) {
            if (imageSrc.startsWith('blob:')) {
                URL.revokeObjectURL(imageSrc);
            }
        }

        setImageSrc(null);
        setUploadedImage(null);
        setCroppedAreaPixels(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setFileSizeError(false);
        setIsEditingExistingPhoto(false);
        setOriginalFile(null);

        // Clear file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        // Force garbage collection by nullifying any potential references
        setTimeout(() => {
            if (window.gc) {
                window.gc();
            }
        }, 100);
    };

    useEffect(() => {
        if (resetTrigger) {
            resetImageUpload();
        }
    }, [resetTrigger]);

    // Add this in your component after the mutations
    useEffect(() => {
        const checkForLeaks = () => {
            const bodyBg = getComputedStyle(document.body).backgroundImage;
            const htmlBg = getComputedStyle(document.documentElement).backgroundImage;
            const rootBg = document.getElementById('root') ?
                getComputedStyle(document.getElementById('root')).backgroundImage : 'none';

            if (bodyBg !== 'none' || htmlBg !== 'none' || rootBg !== 'none') {
                console.warn('Background image leak detected!');
                // Force clear
                document.body.style.backgroundImage = 'none';
                document.documentElement.style.backgroundImage = 'none';
                if (document.getElementById('root')) {
                    document.getElementById('root').style.backgroundImage = 'none';
                }
            }
        };

        checkForLeaks();
        const interval = setInterval(checkForLeaks, 1000);
        return () => clearInterval(interval);
    }, []);

    // Clean up object URLs to prevent memory leaks and style bleeding
    useEffect(() => {
        return () => {
            if (imageSrc && (imageSrc.startsWith('blob:') || imageSrc.startsWith('data:'))) {
                if (imageSrc.startsWith('blob:')) {
                    URL.revokeObjectURL(imageSrc);
                }
                // Clear any potential background image leaks
                document.body.style.backgroundImage = 'none';
                document.documentElement.style.backgroundImage = 'none';
            }
        };
    }, [imageSrc]);

    // Global cleanup on component unmount
    useEffect(() => {
        return () => {
            // Ensure no image data leaks on unmount
            document.body.style.backgroundImage = 'none';
            document.documentElement.style.backgroundImage = 'none';
            const rootElement = document.getElementById('root');
            if (rootElement) {
                rootElement.style.backgroundImage = 'none';
            }
        };
    }, []);

    const hasValidProfilePicture = () => {
        const currentImage = imageSrc || uploadedImage || initialImage;
        return (
            currentImage &&
            currentImage !== "" &&
            currentImage !== UserProfileUserIcon &&
            currentImage !== null &&
            currentImage !== undefined
        );
    };

    const hoverText = hasValidProfilePicture() ? ["Change", "Photo"] : ["Add", "Photo"];

    const handleProfileImageClick = () => {
        if (hasValidProfilePicture()) {
            setImageSrc(initialImage || uploadedImage);
            setFileSizeError(false);
            setIsEditingExistingPhoto(true);
            setCropModalVisible(true);
        } else {
            setIsEditingExistingPhoto(false);
            if (fileInputRef.current) {
                fileInputRef.current.click();
            }
        }
    };

    const handleChangePhotoClick = () => {
        setIsEditingExistingPhoto(false);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRemovePhoto = () => {
        setConfirmModalVisible(true);
    };

    const cancelRemovePhoto = () => {
        setConfirmModalVisible(false);
    };

    const handleCancel = () => {
        // Clean up any existing image sources before closing
        if (imageSrc && (imageSrc.startsWith('data:') || imageSrc.startsWith('blob:'))) {
            if (imageSrc.startsWith('blob:')) {
                URL.revokeObjectURL(imageSrc);
            }
        }

        setCropModalVisible(false);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setFileSizeError(false);
        setIsEditingExistingPhoto(false);
        setOriginalFile(null);

        // Clear file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                style={{ display: "none" }}
                onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                        handleImageChange({ file: e.target.files[0] });
                    }
                }}
            />

            <div
                className="profile-image-upload-wrapper"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleProfileImageClick}
                style={{ cursor: "pointer" }}
            >
                <div className="profile-image-upload-circle">
                    {imageSrc ? (
                        <img src={imageSrc} alt="Preview" className="uploaded-image" />
                    ) : uploadedImage && uploadedImage !== "" ? (
                        <img src={uploadedImage} alt="Existing Profile" className="uploaded-image" />
                    ) : initialImage && initialImage !== "" ? (
                        <img src={initialImage} alt="Existing Profile" className="uploaded-image" />
                    ) : (
                        <img src={UserProfileUserIcon} alt="Upload Icon" className="upload-icon" />
                    )}
                    {isHovered && (
                        <div className="hover-overlay">
                            <div className="hover-content">
                                <span className="hover-text-top">{hoverText[0]}</span>
                                <span className="hover-text-bottom">{hoverText[1]}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal
                open={cropModalVisible}
                onCancel={handleCancel}
                title="Crop Image"
                width={600}
                footer={null}
                centered
                destroyOnClose={true} // Add this to ensure modal is properly cleaned up
            >
                <div className="crop-modal-content">
                    <div style={{ position: "relative", height: "250px" }}>
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                    <div className="changePhotoGroup">
                        <button
                            className="changePhoto"
                            key="change"
                            type="default"
                            onClick={handleChangePhotoClick}
                            style={{ marginTop: "1%" }}
                        >
                            Change Photo
                        </button>
                    </div>

                    <div className="cropButtonGroup">
                        <button
                            className="cropRemove"
                            key="remove"
                            onClick={handleRemovePhoto}
                        >
                            Remove
                        </button>
                        <button
                            className="cropCancel"
                            key="cancel"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            className="cropSave"
                            key="save"
                            onClick={getCroppedImage}
                            disabled={fileSizeError}
                            style={{
                                marginLeft: "10px",
                                backgroundColor: fileSizeError ? "#FAC9CA" : "#C12328",
                                borderColor: fileSizeError ? "#FAC9CA" : "#C12328",
                                color: fileSizeError ? "#9B9C9C" : "#FFFFFF",
                            }}
                        >
                            Save
                        </button>
                    </div>
                    <p className="uploadSize">
                        <img src={Hint} alt="Warning" style={{ width: "18px", verticalAlign: "middle", marginRight: "5px" }} />
                        Upload size limit: 5 MB
                    </p>
                    {fileSizeError && (
                        <div className="crop-info">
                            <p className="error-text">
                                <img src={Warning} alt="Warning" style={{ width: "18px", verticalAlign: "middle", marginRight: "5px" }} />
                                Image size is larger than the uploading limit.
                            </p>
                        </div>
                    )}
                </div>
            </Modal>

            <Modal
                open={confirmModalVisible}
                onCancel={cancelRemovePhoto}
                footer={[
                    <div className="confirmRemoveYesAndNo" style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button key="yes" type="primary" onClick={confirmRemovePhoto} className="removeConfirmYes" disabled={removeProfilePicLoading} style={{ opacity: removeProfilePicLoading ? 0.7 : 1, cursor: removeProfilePicLoading ? 'not-allowed' : 'pointer' }}>
                            {removeProfilePicLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Yes'}
                        </button>
                        <button key="no" onClick={cancelRemovePhoto} className="removeConfirmNo">
                            No
                        </button>
                    </div>,
                ]}
                centered
                destroyOnClose={true} // Add this to ensure modal is properly cleaned up
            >
                <p style={{ fontFamily: "Inter", fontSize: "20px" }}>
                    Do you want to remove profile picture and notify <span style={{ fontWeight: "700" }}>user?</span>
                </p>
                <p className="deleteConfirmNote">Note: Remove the photo only if you find it inappropriate!</p>
            </Modal>
        </>
    );
};

export default ProfileImageUploader;